import { Component, type ErrorInfo, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/Button/Button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/Empty/Empty";
import { HStack } from "@/components/layout/HStack";
import { LayoutDashboard, RotateCw, TriangleAlert } from "lucide-react";

const ErrorFallback = () => {
  return (
    <Empty style={{ borderColor: "var(--secondary-border)" }}>
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <TriangleAlert />
        </EmptyMedia>
        <EmptyTitle>Something Went Wrong</EmptyTitle>
        <EmptyDescription>
          This page ran into an unexpected error. Reloading usually clears it.
          If it keeps happening, please report it.
        </EmptyDescription>
      </EmptyHeader>
      <EmptyContent>
        <HStack>
          <Button onClick={() => window.location.reload()}>
            <RotateCw />
            Reload
          </Button>
          <Button asChild variant="outline">
            <Link to="/dashboard" style={{ textDecoration: "none" }}>
              <LayoutDashboard />
              Go To Dashboard
            </Link>
          </Button>
        </HStack>
      </EmptyContent>
    </Empty>
  );
};

class ErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Uncaught render error:", error, info.componentStack);
  }

  render() {
    if (this.state.hasError) return <ErrorFallback />;
    return this.props.children;
  }
}

// Keyed on the pathname so a crashed route resets itself once the user
// navigates elsewhere, instead of staying stuck on the fallback.
const RouteErrorBoundary = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  return <ErrorBoundary key={pathname}>{children}</ErrorBoundary>;
};

export { ErrorBoundary, RouteErrorBoundary };
